import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const METRIC_LABELS = {
  scheduleOverdue: 'Schedule overdue', qualityOpen: 'Open quality', qualityOverdue: 'Quality overdue',
  safetyOpen: 'Open safety', safetyHighCritical: 'Safety high/critical', controlsOpen: 'Open controls',
  controlsOverdue: 'Controls overdue', designPending: 'Design pending',
  evidence360StaleOrNever: '360 stale/never', projectsWithExceptions: 'Projects with exceptions',
};

export const TrendsPanel = ({ portfolioId }) => {
  const [data, setData] = useState(null);
  const [metric, setMetric] = useState('qualityOpen');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/portfolios/${portfolioId}/trends`, { params: { days: 30 } })
      .then((r) => setData(r.data))
      .catch(() => toast.error('Failed to load trends'))
      .finally(() => setLoading(false));
  }, [portfolioId]);

  if (loading) return <div className="py-12 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  if (!data) return null;
  const series = data.series[metric] || [];
  const max = Math.max(1, ...series.map((p) => p.value));

  return (
    <div className="space-y-4" data-testid="trends-panel">
      <div className="flex items-center gap-3">
        <TrendingUp className="w-5 h-5 text-indigo-600" />
        <select value={metric} onChange={(e) => setMetric(e.target.value)} className="px-3 py-2 text-sm bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-lg" data-testid="trend-metric-select">
          {data.metrics.map((m) => <option key={m} value={m}>{METRIC_LABELS[m] || m}</option>)}
        </select>
        <span className="text-xs text-slate-400">{data.points} day-point(s) captured</span>
      </div>
      {series.length === 0 ? (
        <p className="text-sm text-slate-500 py-8 text-center" data-testid="trends-empty">No history yet — points accumulate once per day as the command center is opened. No trend is fabricated.</p>
      ) : (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-5">
          <div className="flex items-end gap-2 h-40" data-testid="trend-bars">
            {series.map((p) => (
              <div key={p.day} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${p.day}: ${p.value}`}>
                <span className="text-[10px] text-slate-500 tabular-nums">{p.value}</span>
                <div className="w-full bg-indigo-500/80 rounded-t" style={{ height: `${(p.value / max) * 100}%`, minHeight: p.value > 0 ? '4px' : '0' }} />
                <span className="text-[9px] text-slate-400">{p.day.slice(5)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <p className="text-xs text-slate-400">{data.governanceNote}</p>
    </div>
  );
};

export const ExceptionCenterPanel = ({ portfolioId }) => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(true);

  const load = (d) => {
    setLoading(true);
    axios.get(`${API}/portfolios/${portfolioId}/exception-center`, { params: d ? { domain: d } : {} })
      .then((r) => setData(r.data))
      .catch(() => toast.error('Failed to load exceptions'))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(domain); /* eslint-disable-next-line */ }, [portfolioId, domain]);

  if (loading && !data) return <div className="py-12 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  if (!data) return null;

  return (
    <div className="space-y-4" data-testid="exception-center-panel">
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => setDomain('')} className={`text-xs px-3 py-1.5 rounded-full ${!domain ? 'bg-indigo-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600'}`} data-testid="exc-domain-all">All</button>
        {data.domains.map((d) => (
          <button key={d} onClick={() => setDomain(d)} className={`text-xs px-3 py-1.5 rounded-full ${domain === d ? 'bg-indigo-600 text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-600'}`} data-testid={`exc-domain-${d}`}>{d}</button>
        ))}
      </div>
      {data.entries.length === 0 ? (
        <p className="text-sm text-slate-500 py-8 text-center" data-testid="exceptions-empty">No exceptions for this scope.</p>
      ) : (
        <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 divide-y divide-slate-100 dark:divide-slate-800">
          {data.entries.map((e, i) => (
            <button key={i} onClick={() => e.link && navigate(e.link)} className="w-full flex items-center justify-between px-5 py-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 text-left" data-testid="exception-entry">
              <div>
                <span className="font-medium text-slate-900 dark:text-white">{e.projectName}</span>
                <span className="ml-2 text-xs px-2 py-0.5 rounded bg-slate-100 dark:bg-slate-800 text-slate-500">{e.domain} · {e.state}</span>
              </div>
              <span className="text-lg font-bold text-slate-800 dark:text-slate-100 tabular-nums">{e.count}</span>
            </button>
          ))}
        </div>
      )}
      <p className="text-xs text-slate-400">{data.governanceNote}</p>
    </div>
  );
};

const STATE_STYLE = {
  not_configured: 'text-slate-400 italic',
  not_comparable: 'text-amber-600',
  restricted: 'text-slate-400 italic',
  ok: 'text-slate-800 dark:text-slate-100',
};

export const ComparePanel = ({ portfolioId }) => {
  const [projects, setProjects] = useState([]);
  const [selected, setSelected] = useState({});
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/portfolios/${portfolioId}/projects`).then((r) => setProjects(r.data)).catch(() => {});
  }, [portfolioId]);

  const run = () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (ids.length < 2) { toast.error('Select at least 2 projects'); return; }
    setBusy(true);
    axios.post(`${API}/portfolios/${portfolioId}/compare`, { projectIds: ids })
      .then((r) => setResult(r.data))
      .catch(() => toast.error('Comparison failed'))
      .finally(() => setBusy(false));
  };

  const renderCell = (row, cell) => {
    if (!cell) return <span className="text-slate-400">—</span>;
    if (row.type === 'progress') {
      if (cell.state === 'not_configured') return <span className={STATE_STYLE.not_configured} data-testid="compare-not-configured">Not configured</span>;
      if (cell.state === 'restricted') return <span className={STATE_STYLE.restricted}>restricted</span>;
      const v = cell.value;
      return <span className={cell.state === 'not_comparable' ? STATE_STYLE.not_comparable : STATE_STYLE.ok} data-testid={cell.state === 'not_comparable' ? 'compare-not-comparable' : undefined}>{v.reviewedVisual}%{cell.state === 'not_comparable' && <span className="ml-1 text-[10px]">(not comparable)</span>}</span>;
    }
    if (cell.state === 'restricted') return <span className={STATE_STYLE.restricted}>N/A</span>;
    return <span className={STATE_STYLE.ok}>{String(cell.value)}</span>;
  };

  return (
    <div className="space-y-4" data-testid="compare-panel">
      <div className="flex flex-wrap gap-2">
        {projects.map((p) => (
          <label key={p.id} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border cursor-pointer text-sm ${selected[p.id] ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20' : 'border-slate-200 dark:border-slate-700'}`} data-testid="compare-project-option">
            <input type="checkbox" checked={!!selected[p.id]} onChange={(e) => setSelected({ ...selected, [p.id]: e.target.checked })} data-testid={`compare-check-${p.id}`} />
            {p.name}
          </label>
        ))}
      </div>
      <button onClick={run} disabled={busy} className="bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2" data-testid="run-compare-button">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Compare'}</button>

      {result && result.projects.length > 0 && (
        <div className="overflow-x-auto bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800" data-testid="compare-table">
          <table className="w-full text-sm min-w-[600px]">
            <thead>
              <tr className="text-left text-xs uppercase text-slate-500 border-b border-slate-200 dark:border-slate-800">
                <th className="px-4 py-3">Metric</th>
                {result.projects.map((p) => <th key={p.projectId} className="px-4 py-3">{p.name}</th>)}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {result.rows.map((row) => (
                <tr key={row.metric} data-testid={`compare-row-${row.metric}`}>
                  <td className="px-4 py-3 font-medium text-slate-600 dark:text-slate-300">{row.label}</td>
                  {result.projects.map((p) => <td key={p.projectId} className="px-4 py-3 font-semibold tabular-nums">{renderCell(row, row.cells[p.projectId])}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {result && <p className="text-xs text-slate-400">{result.governanceNote}</p>}
    </div>
  );
};
